import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { Calendar, CalendarModule } from 'primeng/calendar';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { DashboardGraphComponent } from '../dashboard-graph/dashboard-graph.component';
import { DashboardApiService } from '@app/areas/dashboard/services/dashboard.api.service';
import {
  ActivityRowView,
  LocationActivityRange,
  LocationIncomePoint,
} from '@app/areas/dashboard/models/activity-report.model';
import { ChartDataset, DashboardChartData } from '@app/areas/dashboard/models/chart.model';
import { ColorsEnum } from '@app/areas/dashboard/enums/colors.enum';
import {
  buildBars,
  comparisonWindowFor,
  IncomeBar,
  IncomeLevel,
  nextLevel,
  rootLevel,
  timeLabel,
  transactionsInBar,
} from '@app/areas/dashboard/utils/income-bars.util';
import { currency } from '@app/areas/dashboard/utils/format.util';

const OUT_OF_RANGE_COLOR = '#c4ccd6';

/**
 * Per-merchant income drill-down modal. Adaptive granularity (month->week->day->hour),
 * click-a-bar to drill, hour-level transaction breakdown, activity-range greying,
 * and compare-to a second same-duration period.
 */
@Component({
  selector: 'app-dashboard-income-chart',
  templateUrl: './dashboard-income-chart.component.html',
  styleUrls: ['./dashboard-income-chart.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, CalendarModule, DashboardGraphComponent],
})
export class DashboardIncomeChartComponent implements OnChanges {
  private readonly dashboardApiService = inject(DashboardApiService);

  @Input() visible = false;
  @Input() row: ActivityRowView | null = null;
  /** Current dashboard window (UTC ISO), the root level for the drill-down. */
  @Input() fromIso: string | null = null;
  @Input() toIso: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();

  protected loading = false;
  protected error: string | null = null;

  protected readonly chartData$ = new BehaviorSubject<DashboardChartData>({ labels: [], datasets: [] });
  protected levelStack: IncomeLevel[] = [];
  private sourceStack: LocationIncomePoint[][] = [];
  private points: LocationIncomePoint[] = [];
  private range: LocationActivityRange | null = null;
  private currentBars: IncomeBar[] = [];
  private currentCompareBars: IncomeBar[] = [];

  // Compare-to state
  @ViewChild('compareCalendar') private compareCalendar?: Calendar;
  protected showComparePicker = false;
  protected compareAnchor: Date | null = null;
  private comparePoints: LocationIncomePoint[] | null = null;

  // Hour-level transaction breakdown
  protected breakdownVisible = false;
  protected breakdownLabel = '';
  protected breakdownTxns: { time: string; amount: string }[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['visible'] || changes['row']) && this.visible && this.row && this.fromIso && this.toIso) {
      this.load();
    }
  }

  get currentLevel(): IncomeLevel | null {
    return this.levelStack.length ? this.levelStack[this.levelStack.length - 1] : null;
  }

  get canGoBack(): boolean {
    return this.levelStack.length > 1;
  }

  get compareDefaultDate(): Date {
    return this.currentLevel?.start ?? new Date();
  }

  private get currentSource(): LocationIncomePoint[] {
    return this.sourceStack[this.sourceStack.length - 1] ?? this.points;
  }

  private load(): void {
    if (!this.row || !this.fromIso || !this.toIso) {
      return;
    }
    const from = new Date(this.fromIso);
    const to = new Date(this.toIso);
    this.loading = true;
    this.error = null;
    this.comparePoints = null;
    this.compareAnchor = null;

    forkJoin({
      points: this.dashboardApiService.getMerchantIncome(this.row.merchantId, this.fromIso, this.toIso).pipe(take(1)),
      range: this.dashboardApiService.getMerchantActivityRange(this.row.merchantId).pipe(take(1)),
    }).subscribe({
      next: ({ points, range }) => {
        this.points = points ?? [];
        this.range = range;
        this.levelStack = [rootLevel(from, to)];
        this.sourceStack = [this.points];
        this.loading = false;
        this.rebuild();
      },
      error: () => {
        this.loading = false;
        this.error = 'Failed to load income analytics for this merchant.';
      },
    });
  }

  onBarSelect(selection: { index: number; datasetIndex: number }): void {
    const level = this.currentLevel;
    if (!level) {
      return;
    }

    const isCompareClick = !!this.comparePoints && !!this.compareAnchor && selection.datasetIndex === 1;
    const bars = isCompareClick ? this.currentCompareBars : this.currentBars;
    const bar = bars[selection.index];
    if (!bar) {
      return;
    }
    const seriesPoints = isCompareClick ? this.comparePoints ?? [] : this.currentSource;

    if (level.granularity === 'hour') {
      this.openBreakdown(bar, seriesPoints);
      return;
    }
    const next = nextLevel(level, bar);
    if (next) {
      this.levelStack = [...this.levelStack, next];
      this.sourceStack = [...this.sourceStack, seriesPoints];
      this.compareAnchor = null;
      this.comparePoints = null;
      this.rebuild();
    }
  }

  back(): void {
    if (this.canGoBack) {
      this.levelStack = this.levelStack.slice(0, -1);
      this.sourceStack = this.sourceStack.slice(0, -1);
      this.compareAnchor = null;
      this.comparePoints = null;
      this.rebuild();
    }
  }

  openComparePicker(): void {
    this.showComparePicker = !this.showComparePicker;
    if (this.showComparePicker) {
      // Focus the input once *ngIf has rendered it; PrimeNG opens the overlay on focus
      // (showOnFocus), so "Compare to…" reveals the date picker in a single click.
      setTimeout(() => this.compareCalendar?.inputfieldViewChild?.nativeElement?.focus());
    }
  }

  applyCompare(): void {
    const level = this.currentLevel;
    if (!this.row || !this.compareAnchor || !level) {
      return;
    }
    const { from, to } = comparisonWindowFor(level, this.compareAnchor);
    this.showComparePicker = false;
    this.dashboardApiService
      .getMerchantIncome(this.row.merchantId, from.toISOString(), to.toISOString())
      .pipe(take(1))
      .subscribe((points) => {
        this.comparePoints = points ?? [];
        this.rebuild();
      });
  }

  clearCompare(): void {
    this.compareAnchor = null;
    this.comparePoints = null;
    this.rebuild();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private openBreakdown(bar: IncomeBar, points: LocationIncomePoint[]): void {
    const txns = transactionsInBar(points, bar);
    this.breakdownLabel = bar.label;
    this.breakdownTxns = txns.map((t) => ({ time: timeLabel(t.timestamp), amount: currency(t.amount) }));
    this.breakdownVisible = true;
  }

  private rebuild(): void {
    const level = this.currentLevel;
    if (!level) {
      this.currentBars = [];
      this.currentCompareBars = [];
      this.chartData$.next({ labels: [], datasets: [] });
      return;
    }

    const primaryBars = buildBars(this.currentSource, level, this.range);

    const comparing = !!this.comparePoints && !!this.compareAnchor;
    let compareBars: IncomeBar[] = [];
    if (comparing) {
      const { from, to } = comparisonWindowFor(level, this.compareAnchor!);
      compareBars = buildBars(
        this.comparePoints!,
        { label: '', granularity: level.granularity, start: from, endExclusive: to },
        null
      );
    }

    // Trim leading/trailing empty buckets. When comparing, keep any bucket where EITHER series
    // has activity, so a comparison period whose transactions fall on different hours/days than
    // the primary is still plotted (both lines share the union axis) instead of collapsing to zero.
    const span = Math.max(primaryBars.length, compareBars.length);
    const hasAmount = (i: number) => (primaryBars[i]?.amountTotal ?? 0) > 0 || (compareBars[i]?.amountTotal ?? 0) > 0;

    let firstNonZero = -1;
    for (let i = 0; i < span; i++) {
      if (hasAmount(i)) {
        firstNonZero = i;
        break;
      }
    }
    if (firstNonZero === -1) {
      this.currentBars = [];
      this.currentCompareBars = [];
      this.chartData$.next({ labels: [], datasets: [] });
      return;
    }
    let lastNonZero = span - 1;
    while (lastNonZero > firstNonZero && !hasAmount(lastNonZero)) {
      lastNonZero--;
    }

    this.currentBars = primaryBars.slice(firstNonZero, lastNonZero + 1);
    this.currentCompareBars = comparing ? compareBars.slice(firstNonZero, lastNonZero + 1) : [];

    const labels: string[] = [];
    for (let i = firstNonZero; i <= lastNonZero; i++) {
      labels.push(primaryBars[i]?.label ?? compareBars[i]?.label ?? '');
    }

    const datasets: ChartDataset[] = [
      {
        label: 'Amount',
        data: labels.map((_, k) => round2(this.currentBars[k]?.amountTotal ?? 0)),
        borderColor: ColorsEnum.LIGHT_BLUE,
        // Per-point dots: greyed where the period falls outside the merchant's activity range.
        pointBackgroundColor: labels.map((_, k) =>
          this.currentBars[k]?.inRange === false ? OUT_OF_RANGE_COLOR : ColorsEnum.LIGHT_BLUE
        ),
        pointLabels: labels,
        fill: false,
        tension: 0,
      },
    ];

    if (comparing) {
      datasets.push({
        label: 'Comparison',
        data: labels.map((_, k) => round2(this.currentCompareBars[k]?.amountTotal ?? 0)),
        borderColor: ColorsEnum.DOC_PURPLE,
        pointBackgroundColor: ColorsEnum.DOC_PURPLE,
        pointLabels: labels,
        fill: false,
        tension: 0,
      });
    }

    this.chartData$.next({ labels, datasets });
  }
}

function round2(n: number): number {
  return Math.round((n ?? 0) * 100) / 100;
}
